import { AnimatePresence, motion, useInView, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import Lenis from "lenis";
import { createContext, FormEvent, useContext, useEffect, useRef, useState } from "react";
import { ES, LANG_KEY, initialLang, type Lang } from "./i18n";
import "./service-chats.css";

const A = "/assets/source/";

// Where "Work" and "See Our Work" point, and where the contact form posts. Both live on the portfolio service.
const PORTFOLIO_URL = "https://portfolio.markandtingagency.com/";
const CONTACT_ENDPOINT = "https://portfolio.markandtingagency.com/api/contact";
// The hero gallery plays light clips of the portfolio's Content Production videos.
const INSTAGRAM_URL = "https://www.instagram.com/markandtingagency";
const interests = ["Content & Ads", "Website", "Automation & AI", "Real estate media", "Not sure yet"];
const PORTFOLIO_ITEMS = new URL("api/portfolio/items", PORTFOLIO_URL.endsWith("/") ? PORTFOLIO_URL : `${PORTFOLIO_URL}/`).toString();

const nav = [
  ["Home", "header"], ["Services", "services"], ["Why us", "compare"],
  ["Reviews", "reviews"], ["Team", "team"], ["FAQs", "faqs"],
  ["Onboarding", "onboarding"], ["Contact", "contact"],
];

const gallery = [
  "AIoOfGAMorkJjQmj2tX7TrOu3iA.jpg", "09ucYxzynkNazR7K8tqfYJOIazs.jpeg",
  "Q5593UengmjCPDObjrtmRanCt1I.jpg", "5hiNETax6RJrOnGME3MkBU2Nk.jpg",
  "YuOhQNA0BwXAFO9jltpX9YP2rPo.jpg", "wmovVbWoFmeH7kNLQxdXnazYzaw.jpg",
  "VhNFGVmT7SnXpfq927JltZbV1A.jpg", "UAXYquxR6CJELtIQM8umss9GDl4.jpg",
];

const clientLogos = [
  "mhBIUYHxEZPHnQn0U2cNkOi9Y.png", "tVxnpIY5u85GFHTlJqgEwhJ3RXE.png", "iTefnFH1L6KXpJ9VXCIiPSoQiQ.png",
  "96CYr0Q5TDioIe9otmo0V2JOLTM.png", "YZbP5QywErGwdOrAzdcNqisCBg.png", "NbDlZpTSaw7eVHoKjH3XQoMDsY.png",
  "GBlCBMjmbaqIvY7N0dyrN0pt3QI.png", "jLh0zi39jTEAzJ73hnkhcMf39yA.png", "R5EmbQoOLIDxwYVhXRREGducpCY.png", "OukPKtMUdyA3qtEkECMq51THvIk.png",
];

const services = [
  {
    badge: "Video Production",
    title: "Short-Form Video & Content Production",
    text: "High-retention vertical videos, professional drone footage, and visual storytelling built for Instagram, TikTok, and YouTube Shorts.",
    bullets: ["Monthly filming with professional camera gear", "High-tempo editing, typography and sound design", "4K drone footage for exteriors and commercial spaces", "Content calendars planned around buyer intent"],
    visual: "video",
    work: "video",
  },
  {
    badge: "Paid Media",
    title: "High-Converting Paid Advertising",
    text: "Targeted campaigns across Meta and Google that place your offer in front of ready-to-buy local and regional customers.",
    bullets: ["Meta Ads across feeds, stories and reels", "Google Search and local campaigns", "Direct copywriting and ongoing creative testing", "Conversion tracking and clear performance reporting"],
    visual: "ads",
    work: "ads",
  },
  {
    badge: "AI & Systems",
    title: "Growth Systems & AI Implementation",
    text: "Custom lead capture workflows, CRM pipelines, and automated assistants that help your team respond while prospects are still interested.",
    bullets: ["Automated WhatsApp and Instagram DM responses", "CRM pipelines with automated follow-ups", "AI assistants that answer and qualify leads day or night", "Lead routing directly to your sales team"],
    visual: "automation",
    work: "automation",
  },
  {
    badge: "Web & CRO",
    title: "Websites & Conversion Landing Pages",
    text: "Custom web experiences designed to tell your story, strengthen organic visibility, and turn visits into qualified enquiries.",
    bullets: ["Fast, mobile-first builds", "Clear conversion and booking pathways", "A clean technical SEO foundation", "Contact and quote capture connected to your workflow"],
    visual: "web",
    work: "web",
  },
  {
    badge: "Real Estate",
    title: "Real Estate Media",
    text: "Listing walkthroughs, aerial property media, and personal brand content for realtors, builders, and properties that need to stand out.",
    bullets: ["4K property walkthrough videos", "HDR interior and exterior photography", "Drone footage that shows context and scale", "Agent content that builds local authority"],
    visual: "real-estate",
    work: "video",
  },
];

const comparisonRows = [
  ["Content, ads, web and automation under one strategy", true, false, false, false],
  ["Our own crew for video, photo and drone", true, false, true, false],
  ["Automated lead follow-up (WhatsApp, DMs, CRM)", true, false, false, false],
  ["Campaigns in English and Spanish", true, false, false, false],
  ["No long-term contracts", true, false, true, false],
  ["Direct line to the people doing the work", true, true, true, false],
  ["No hiring, training or managing staff", true, false, false, true],
  ["Focused on leads and sales, not just likes", true, true, false, false],
] as const;

const reviews = [
  ["Mark & Ting has been a great partner for Tu Clínica Familiar. Their work goes beyond marketing — they bring strategy, creativity, and systems that help a business grow with more structure and consistency.", "Esteban Torres", "Co Founder TCF", "eU0eE1uH2s48zEZ2CvBgNbNmM1Y.jpeg"],
  ["What we value most is that they don’t just create content — they think about how to position the business, improve visibility, and build systems that support long-term growth.", "Gerardo Romero", "Romeros Auto Center & Superior Founder", "OS4kQqWVoAz7BhW5muuEOyaB6s.jpeg"],
  ["Their work has helped us improve our image, stay more consistent, and approach growth in a more professional and strategic way.", "Marcos Moreno", "CEO & Founder TIPS Plant Services", "LP3tY1VU63Loeiw2m8ahNTubMwQ.jpeg"],
  ["They understand how important trust and positioning are for businesses in real estate and lending. Their support has helped us strengthen our presence.", "Kimian Ng", "Senior Mortgage Banker Flat Branch", "I4eD4HMB8QiaTaGTRrT4nLC9Y8.jpg"],
];

const faqs = [
  ["How does working with Mark & Ting start?", "We start with a discovery conversation to understand your business, goals, and where you may be losing visibility, leads, or opportunities. From there, we recommend the right growth system."],
  ["What types of businesses do you work with?", "We work primarily with service-based businesses and realtors looking to generate more clients through content, ads, and automation systems."],
  ["Do I need to commit to a long-term contract?", "No. We focus on real partnerships, but we don’t believe in locking businesses into something that doesn’t create value."],
  ["What services can be included in my package?", "Depending on your needs, your package can include content creation, social media management, Meta Ads, Google Ads, landing pages, branding, websites, automation, CRM setup, and lead follow-up systems."],
  ["Do you only help with social media?", "No. We help businesses grow with content, ads, and automation that work together as one system."],
  ["How do I know which package is right for my business?", "That’s exactly what the strategy call is for. We’ll help identify what makes sense based on your stage and goals."],
];

const steps = [
  ["Discovery & Alignment", "We begin with a strategic conversation to understand your business, identify opportunities, and determine if we’re the right fit."],
  ["Onboarding & Access", "We collect everything needed to build the right system — goals, offer, platforms, branding, and essential access."],
  ["Strategy & System Setup", "We define the strategy and build the technical infrastructure needed to attract, capture, and convert leads."],
  ["Launch & Execution", "We walk you through the setup, align on execution, launch, and move into ongoing optimization and growth."],
];

const team = [
  {
    name: "Yedan",
    role: "Founder",
    image: "/assets/team/yedan.webp",
    bio: "An innovative leader with a background in Financial Direction from Universidad Anáhuac CDMX and experience at Nu Bank. After studying in Rome and earning recognition at the Unilever Future Leaders League in London, he now builds systems that drive growth and empower the business community.",
  },
  {
    name: "Yury",
    role: "Co-Founder",
    image: "/assets/team/yury.webp",
    bio: "A growth-driven leader with a background in International Business from UIW and an MBA from MCI. With experience in Germany, Austria and finance at Grupo Salinas, he focuses on systems that help businesses scale sustainably.",
  },
  {
    name: "Genesys",
    role: "Director of Operations",
    image: "/assets/team/genesys.webp",
    bio: "Keeps every project moving, from planning and timelines to delivery, so each client always knows what comes next.",
  },
  {
    name: "Bárbara",
    role: "Community Manager",
    image: "/assets/team/barbara.webp",
    bio: "A digital marketing enthusiast who believes social media is for connecting, not just posting. Her approach puts strategy over gimmicks and turns online engagement into loyal customers.",
  },
  {
    name: "Gastón",
    role: "Video Editor",
    image: "/assets/team/gaston.webp",
    bio: "The creative eye behind our clients' scroll-stopping videos. He edits dynamic, high-retention content designed to make an immediate impact and support measurable business results.",
  },
  {
    name: "Israel",
    role: "Programmer",
    image: "/assets/team/israel.webp",
    bio: "The wizard behind the code. He builds distinctive websites engineered for speed, conversion and strong organic visibility.",
  },
];

// ---------- Language (EN | ES): the Spanish copy lives in src/i18n.ts, keyed by the English text ----------
const LangContext = createContext<{ lang: Lang; setLang: (lang: Lang) => void }>({ lang: "en", setLang: () => undefined });
function useLang() { return useContext(LangContext); }
function translate(lang: Lang, text: string) { return lang === "es" ? ES[text] ?? text : text; }
function useT() { const { lang } = useLang(); return (text: string) => translate(lang, text); }
/** The portfolio opens in the landing's language. */
function portfolioHref(lang: Lang, hash = "") {
  const url = new URL(PORTFOLIO_URL);
  url.searchParams.set("lang", lang);
  url.hash = hash;
  return url.toString();
}
function usePortfolioHref() { const { lang } = useLang(); return (hash = "") => portfolioHref(lang, hash); }
/** Client portals and the admin sign in here, in the landing's language. */
function loginHref(lang: Lang) {
  const url = new URL("acceso", PORTFOLIO_URL);
  url.searchParams.set("lang", lang);
  return url.toString();
}

function LangSwitch({ className = "" }: { className?: string }) {
  const { lang, setLang } = useLang();
  const t = useT();
  return <div className={`lang-switch ${className}`} role="group" aria-label={t("Language")}>
    <button type="button" className={lang === "en" ? "is-active" : ""} aria-pressed={lang === "en"} onClick={() => setLang("en")}>EN</button>
    <span aria-hidden="true">|</span>
    <button type="button" className={lang === "es" ? "is-active" : ""} aria-pressed={lang === "es"} onClick={() => setLang("es")}>ES</button>
  </div>;
}

function Reveal({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const reduce = useReducedMotion();
  return <motion.div className={className} initial={reduce ? false : { opacity: 0, y: 34, scale: .985, filter: "blur(8px)" }} whileInView={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }} viewport={{ once: true, margin: "-10%" }} transition={{ duration: .78, delay, ease: [.22, 1, .36, 1] }}>{children}</motion.div>;
}

function TiltCard({ children, className = "", lift = 0 }: { children: React.ReactNode; className?: string; lift?: number }) {
  const reduce = useReducedMotion();
  const rotateX = useSpring(useMotionValue(0), { stiffness: 180, damping: 22, mass: .7 });
  const rotateY = useSpring(useMotionValue(0), { stiffness: 180, damping: 22, mass: .7 });
  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (reduce || event.pointerType === "touch") return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - .5;
    const y = (event.clientY - rect.top) / rect.height - .5;
    rotateY.set(x * 5);
    rotateX.set(y * -5);
  };
  const onPointerLeave = () => { rotateX.set(0); rotateY.set(0); };
  return <motion.article className={className} style={{ rotateX, rotateY, transformPerspective: 1100 }} onPointerMove={onPointerMove} onPointerLeave={onPointerLeave} whileHover={{ y: lift, scale: 1.008 }} transition={{ type: "spring", stiffness: 180, damping: 20 }}>{children}</motion.article>;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  // Clamped: iOS's bounce at the end pushes the progress past 1, which made phones scroll sideways a few pixels.
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return <motion.div className="scroll-progress" style={{ scaleX }} aria-hidden="true" />;
}

function Pill({ children }: { children: React.ReactNode }) {
  const reduce = useReducedMotion();
  const text = typeof children === "string" ? children : "";
  const [count, setCount] = useState(reduce ? text.length : 0);
  const [active, setActive] = useState(Boolean(reduce));
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (reduce) {
      setActive(true);
      setCount(text.length);
      return;
    }
    const node = ref.current;
    if (!node || !text) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setActive(true);
        observer.disconnect();
      }
    }, { threshold: .65 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduce, text]);

  useEffect(() => {
    if (!active || reduce || count >= text.length) return;
    const timer = window.setTimeout(() => setCount(value => value + 1), 42);
    return () => window.clearTimeout(timer);
  }, [active, count, reduce, text.length]);

  return <span ref={ref} className="pill" aria-label={text || undefined}><i /><span className={`pill-label ${active && count < text.length ? "typing" : ""}`}>{text ? text.slice(0, count) : children}</span><i /></span>;
}

function Button({ href, children, light = false }: { href: string; children: React.ReactNode; light?: boolean }) {
  return <motion.a className={`button ${light ? "button-light" : "button-dark"} ${href === "#contact" ? "button-cta" : ""}`} href={href} whileHover={{ y: -3, scale: 1.015 }} whileTap={{ scale: .98 }}><span>{children}</span><b className="button-icon" aria-hidden="true"><i /><i /><i /><i /></b></motion.a>;
}

function InstagramIcon() {
  return <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4.2" /><circle cx="17.4" cy="6.6" r="1.1" fill="currentColor" stroke="none" /></svg>;
}

/** Next to the main calls to action: the agency posts most of its work on Instagram. */
function InstagramButton({ label = "See our Instagram" }: { label?: string }) {
  const t = useT();
  return <motion.a className="button button-light button-instagram" href={INSTAGRAM_URL} target="_blank" rel="noopener" whileHover={{ y: -3, scale: 1.015 }} whileTap={{ scale: .98 }}><span>{t(label)}</span><b className="button-icon" aria-hidden="true"><InstagramIcon /></b></motion.a>;
}

function SectionTitle({ eyebrow, title, children }: { eyebrow: string; title: React.ReactNode; children?: React.ReactNode }) {
  return <div className="section-head"><Reveal><Pill>{eyebrow}</Pill><h2>{title}</h2></Reveal>{children && <Reveal className="section-intro" delay={.08}>{children}</Reveal>}</div>;
}

// 5 logos at a time; every few seconds all 5 change together to 5 that were not on screen, in a random order.
function RandomLogoGrid() {
  const reduce = useReducedMotion();
  const [logos, setLogos] = useState(() => shuffled(clientLogos).slice(0, 5));

  useEffect(() => {
    if (reduce) return;
    const timer = window.setInterval(() => {
      setLogos(current => {
        const fresh = shuffled(clientLogos.filter(logo => !current.includes(logo)));
        return fresh.length >= 5 ? fresh.slice(0, 5) : [...fresh, ...shuffled(current)].slice(0, 5);
      });
    }, 4500);
    return () => window.clearInterval(timer);
  }, [reduce]);

  return <div className="logo-grid">{logos.map((src, index) => <div className="logo-slot" key={index}>
    <AnimatePresence mode="wait" initial={false}>
      <motion.img key={src} src={`/assets/logos/${src.replace(/\.png$/, ".webp")}`} alt="Client brand" initial={{ opacity: 0, scale: .94 }} animate={{ opacity: 1, scale: 1, transition: { duration: .45, delay: index * .07 } }} exit={{ opacity: 0, scale: 1.04, transition: { duration: .3, delay: index * .07 } }} />
    </AnimatePresence>
  </div>)}</div>;
}

type ServiceChatScript = {
  name: string;
  label: string;
  messages: readonly { speaker: "client" | "assistant"; text: string; time: string }[];
};

const serviceChatScripts: Record<string, ServiceChatScript> = {
  video: {
    name: "Mark&Ting Content",
    label: "Example content production conversation",
    messages: [
      { speaker: "client", text: "We need fresh content for Instagram and TikTok.", time: "8:15" },
      { speaker: "assistant", text: "Great! We can film this week and deliver edited reels.", time: "8:15 ✓✓" },
      { speaker: "client", text: "Friday works for us.", time: "8:16" },
      { speaker: "assistant", text: "Perfect — your filming day is booked for Friday at 9:00 AM.", time: "8:16 ✓✓" },
    ],
  },
  automation: {
    name: "Mark&Ting Assistant",
    label: "Example automated WhatsApp conversation",
    messages: [
      { speaker: "client", text: "Hi! I would like to grow my business.", time: "10:24" },
      { speaker: "assistant", text: "Great — what would you like help with first?", time: "10:24 ✓✓" },
      { speaker: "client", text: "More qualified leads.", time: "10:25" },
      { speaker: "assistant", text: "Perfect. I can book a strategy call for tomorrow at 11:00 AM.", time: "10:25 ✓✓" },
    ],
  },
  ads: {
    name: "Mark&Ting Ads",
    label: "Example paid advertising conversation",
    messages: [
      { speaker: "client", text: "How is the new campaign doing?", time: "10:31" },
      { speaker: "assistant", text: "It is bringing in qualified responses, and the strongest creative is clear.", time: "10:31 ✓✓" },
      { speaker: "client", text: "What should we do next?", time: "10:32" },
      { speaker: "assistant", text: "I recommend testing the next variation around that winning angle.", time: "10:32 ✓✓" },
    ],
  },
  web: {
    name: "Mark&Ting Web",
    label: "Example website lead conversation",
    messages: [
      { speaker: "client", text: "Hi! I would like a quote from the website.", time: "9:42" },
      { speaker: "assistant", text: "Happy to help. Would tomorrow afternoon work for a quick consultation?", time: "9:42 ✓✓" },
      { speaker: "client", text: "Yes, that works for me.", time: "9:43" },
      { speaker: "assistant", text: "Perfect — your consultation is booked. We will send the details here.", time: "9:43 ✓✓" },
    ],
  },
  "real-estate": {
    name: "Mark&Ting Media",
    label: "Example real estate media conversation",
    messages: [
      { speaker: "client", text: "I need video and photos for a new listing.", time: "11:08" },
      { speaker: "assistant", text: "Absolutely. When will the property be ready?", time: "11:08 ✓✓" },
      { speaker: "client", text: "Thursday morning works best.", time: "11:09" },
      { speaker: "assistant", text: "Your media session is confirmed for Thursday at 10:00 AM.", time: "11:09 ✓✓" },
    ],
  },
};

type ChatLine = ServiceChatScript["messages"][number];

/** After the chat, the team gets to work: a short scene in M&T colours for each service. */
function WorkScene({ kind, photos }: { kind: string; photos: string[] }) {
  const t = useT();
  if (kind === "web") return <div className="scene scene-web" aria-hidden="true">
    <div className="scene-browser"><div className="scene-browser-bar"><i /><i /><i /><span>{t("yourbusiness.com")}</span></div>
      <div className="scene-site">
        <div className="scene-nav"><b /><i /><i /><i /></div>
        <div className="scene-hero"><div><em className="w80" /><em className="w60" /><u className="w70" /><strong /></div><figure /></div>
        <div className="scene-cards"><span /><span /><span /></div>
        <div className="scene-cursor" />
      </div>
    </div>
  </div>;
  if (kind === "video") return <div className="scene scene-video" aria-hidden="true">
    <div className="scene-editor">
      <div className="scene-preview" style={photos[0] ? { backgroundImage: `url('${photos[0]}')` } : undefined}><span className="scene-rec">{t("Editing")}</span><em>{t("Your reel")}</em></div>
      <div className="scene-tracks">
        <div className="scene-track scene-track-video">{[0, 1, 2, 3].map(n => <span key={n} style={photos[n] ? { backgroundImage: `url('${photos[n]}')` } : undefined} />)}</div>
        <div className="scene-track scene-track-text"><span>{t("Captions")}</span><span>{t("Hook")}</span></div>
        <div className="scene-track scene-track-audio">{Array.from({ length: 18 }, (_, n) => <i key={n} style={{ ["--a" as string]: `${30 + ((n * 37) % 60)}%` }} />)}</div>
        <b className="scene-playhead" />
      </div>
    </div>
  </div>;
  if (kind === "ads") return <div className="scene scene-ads" aria-hidden="true">
    <div className="scene-panel">
      <div className="scene-ads-top"><strong>{t("Campaign")}</strong><span>Meta</span><span>Google</span></div>
      <div className="scene-ads-body">
        <div className="scene-ad"><figure /><em className="w80" /><em className="w50" /><strong /></div>
        <div className="scene-chart">
          <div className="scene-bars"><span style={{ ["--h" as string]: "38%" }} /><span style={{ ["--h" as string]: "52%" }} /><span style={{ ["--h" as string]: "46%" }} /><span style={{ ["--h" as string]: "70%" }} /><span style={{ ["--h" as string]: "88%" }} /></div>
          <svg viewBox="0 0 200 80" preserveAspectRatio="none"><path d="M4 70 C 40 64, 60 52, 90 48 S 150 22, 196 10" /></svg>
        </div>
      </div>
      <div className="scene-kpis"><span>{t("Leads ↑")}</span><span>{t("Cost per lead ↓")}</span><span>{t("Best creative ✓")}</span></div>
    </div>
  </div>;
  return <div className="scene scene-media" aria-hidden="true">
    <div className={`scene-viewfinder${photos[0] ? " has-photo" : ""}`} style={photos[0] ? { backgroundImage: `url('${photos[0]}')` } : undefined}><i /><i /><i /><i /><span className="scene-rec">REC</span><span className="scene-4k">{t("4K · Drone")}</span>{!photos[0] && <div className="scene-horizon" />}</div>
    <div className="scene-gallery">{[1, 2, 3, 4].map(n => <span key={n} style={photos[n] ? { backgroundImage: `url('${photos[n]}')` } : undefined} />)}</div>
    <div className="scene-timeline"><span /><b /></div>
  </div>;
}

const sceneDone: Record<string, string> = { video: "Reel published ✓", web: "Site live ✓", ads: "Campaign optimized ✓", "real-estate": "Listing delivered ✓" };
const sceneStatus: Record<string, string> = { video: "Editing your reels…", web: "Building your site…", ads: "Optimizing your campaign…", "real-estate": "Shooting and editing…" };

/**
 * The conversation plays as if typed live: the client's messages are typed in the box and sent, the assistant shows
 * "typing…" first. Then the chat gives way to a short work scene, and it starts over. Only while on screen; with
 * reduced motion it shows the finished conversation.
 */
function ServiceStory({ kind, script, photos }: { kind: string; script: ServiceChatScript; photos: string[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: .35 });
  const reduce = useReducedMotion();
  const [lines, setLines] = useState<ChatLine[]>(() => (reduce ? [...script.messages] : []));
  const [draft, setDraft] = useState("");
  const [typing, setTyping] = useState(false);
  const [phase, setPhase] = useState<"chat" | "work" | "done">("chat");
  const { lang } = useLang();
  const t = (text: string) => translate(lang, text);
  useEffect(() => {
    if (!inView || reduce) return;
    let cancelled = false;
    const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));
    (async () => {
      while (!cancelled) {
        setLines([]); setDraft(""); setTyping(false); setPhase("chat");
        await wait(600);
        for (const line of script.messages) {
          if (cancelled) return;
          if (line.speaker === "client") {
            const typed = translate(lang, line.text);
            for (let i = 1; i <= typed.length && !cancelled; i++) { setDraft(typed.slice(0, i)); await wait(32); }
            await wait(260);
            setDraft("");
          } else {
            setTyping(true);
            await wait(1150);
            setTyping(false);
          }
          if (cancelled) return;
          setLines(current => [...current, line]);
          await wait(750);
        }
        await wait(900);
        if (cancelled) return;
        // AI & Systems has no work scene: the booked call is the result; it holds, then starts over.
        if (!sceneDone[kind]) { await wait(3200); continue; }
        setPhase("work");
        await wait(6400);
        if (cancelled) return;
        setPhase("done");
        await wait(2200);
      }
    })();
    return () => { cancelled = true; };
  }, [inView, reduce, lang]);

  const working = phase !== "chat";
  return <div ref={ref} className={`automation-mockup service-conversation service-story story-${kind}`} aria-label={script.label}>
    <div className="automation-head"><span>MT</span><div><strong>{t(script.name)}</strong><small>{t(working ? sceneStatus[kind] : typing ? "typing…" : "Online")}</small></div><b>•••</b></div>
    <div className="story-body">
      <AnimatePresence mode="wait" initial={false}>
        {!working
          ? <motion.div key="chat" className="automation-chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: .35 }}>
            {lines.map(line => <motion.div key={line.text} className={`message ${line.speaker === "client" ? "message-client" : "message-bot"}`} initial={{ opacity: 0, y: 10, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: .3 }}>
              <p>{t(line.text)}</p><small>{line.time}</small>
            </motion.div>)}
            {typing && <motion.div key="typing" className="message message-bot message-typing" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} aria-label="typing"><i /><i /><i /></motion.div>}
          </motion.div>
          : <motion.div key="work" className="story-work" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: .4 }}>
            <WorkScene kind={kind} photos={photos} />
            <AnimatePresence>{phase === "done" && <motion.span className="story-done" initial={{ opacity: 0, scale: .8, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}>{t(sceneDone[kind])}</motion.span>}</AnimatePresence>
          </motion.div>}
      </AnimatePresence>
    </div>
    <div className={`automation-input${draft ? " is-typing" : ""}`}><span>{draft || t("Message")}{draft && <i className="story-caret" />}</span><b>↗︎</b></div>
  </div>;
}

/** Video, ads, web and real estate: the team at work, no chat. The scene plays and loops while it is on screen. */
function ServiceScene({ kind, name, photos }: { kind: string; name: string; photos: string[] }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { amount: .35 });
  const reduce = useReducedMotion();
  const [round, setRound] = useState(0);
  const [started, setStarted] = useState(Boolean(reduce));
  const [done, setDone] = useState(Boolean(reduce));
  const t = useT();
  useEffect(() => {
    if (!inView || reduce) return;
    setStarted(true);
    setDone(false);
    const finish = window.setTimeout(() => setDone(true), 6400);
    const next = window.setTimeout(() => setRound(r => r + 1), 8800);
    return () => { window.clearTimeout(finish); window.clearTimeout(next); };
  }, [inView, reduce, round]);
  return <div ref={ref} className={`automation-mockup service-conversation service-story service-scene story-${kind}`} aria-label={t(sceneStatus[kind])}>
    <div className="automation-head"><span>MT</span><div><strong>{t(name)}</strong><small>{t(done ? sceneDone[kind] : sceneStatus[kind])}</small></div><b>•••</b></div>
    <div className="story-body">
      {started && <div className="story-work" key={round}>
        <WorkScene kind={kind} photos={photos} />
        <AnimatePresence>{done && <motion.span className="story-done" initial={{ opacity: 0, scale: .8, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}>{t(sceneDone[kind])}</motion.span>}</AnimatePresence>
      </div>}
    </div>
  </div>;
}

// Real Estate shows real work for realtors: a property first (the viewfinder), then the realtors' own reels.
const REAL_ESTATE_FRAMES = ["TiT0iW5d1Qs", "LXPqXJi9c2w", "TAPucHq8hhU", "62SXvsaw0kM", "Z7YWU72iM_k"];

function ServiceVisual({ service, items }: { service: typeof services[number]; items: PortfolioItem[] }) {
  const script = serviceChatScripts[service.visual];
  if (!script) return null;
  if (service.visual === "automation") return <ServiceStory kind={service.visual} script={script} photos={[]} />;
  const poster = (item: PortfolioItem) => new URL(item.clipPoster!, PORTFOLIO_ITEMS).toString();
  const realEstate = (item: PortfolioItem) => /realty|kimian|empire title|sivira/i.test((item as PortfolioItem & { client?: string }).client ?? "");
  const photos = service.visual === "real-estate"
    ? REAL_ESTATE_FRAMES.map(id => items.find(item => item.youtubeId === id && item.clipPoster)).filter((item): item is PortfolioItem => Boolean(item)).map(poster)
    : items.filter(item => item.clipPoster && !realEstate(item)).slice(0, 4).map(poster);
  return <ServiceScene kind={service.visual} name={script.name} photos={photos} />;
}

function StatusMark({ yes }: { yes: boolean }) {
  const t = useT();
  return <span className={`status-mark ${yes ? "yes" : "no"}`} aria-label={t(yes ? "Included" : "Not included")}>{yes ? "✓" : "✕"}</span>;
}

function ComparisonSection() {
  const competitors = [
    { label: "In-house", long: "In-house team", valueIndex: 2 },
    { label: "Freelancers", long: "Freelancers", valueIndex: 3 },
    { label: "Agencies", long: "Typical agencies", valueIndex: 4 },
  ] as const;
  const [selected, setSelected] = useState(0);
  const competitor = competitors[selected];
  const t = useT();

  return <section className="section comparison-section" id="compare">
    <SectionTitle eyebrow={t("The clear difference")} title={<>{t("Why businesses choose ")}<em>Mark&amp;Ting.</em></>}><p>{t("See how one connected team compares when content, ads, websites, and follow-up need to work together.")}</p></SectionTitle>
    <Reveal>
      <div className="comparison-desktop">
        <table>
          <thead><tr><th>{t("What you get")}</th><th className="mark-column"><strong>Mark&amp;Ting<span className="brand-dot">.</span></strong></th><th>{t("In-house team")}</th><th>{t("Freelancers")}</th><th>{t("Typical agencies")}</th></tr></thead>
          <tbody>{comparisonRows.map(row => <tr key={row[0]}><th scope="row">{t(row[0])}</th>{row.slice(1).map((value, index) => <td key={index} className={index === 0 ? "mark-column" : ""}><StatusMark yes={Boolean(value)} /></td>)}</tr>)}
            <tr className="comparison-cta-row"><td /><td className="mark-column"><Button href="#contact" light><span className="cta-long">{t("Book Strategy Call")}</span><span className="cta-short">{t("Book a call")}</span></Button></td><td /><td /><td /></tr>
          </tbody>
        </table>
      </div>
      <div className="comparison-mobile">
        <div className="comparison-chips" role="tablist" aria-label={t("Choose a comparison")}>{competitors.map((option, index) => <button key={option.label} type="button" role="tab" aria-selected={selected === index} onClick={() => setSelected(index)}>vs. {t(option.label)}</button>)}</div>
        <div className="mobile-compare-head"><strong>Mark&amp;Ting<span className="brand-dot">.</span></strong><span>{t(competitor.long)}</span></div>
        <div className="mobile-compare-list">{comparisonRows.map(row => <article key={row[0]}><h3>{t(row[0])}</h3><div><StatusMark yes={Boolean(row[1])} /><StatusMark yes={Boolean(row[competitor.valueIndex])} /></div></article>)}</div>
        <Button href="#contact">{t("Book Strategy Call")}</Button>
      </div>
    </Reveal>
  </section>;
}

// iPhone/iPad refuse to autoplay in Low Power Mode or with "Auto-Play Video Previews" off, but a tap lets a video play:
// every tap starts the muted clips on screen that are still waiting.
if (typeof document !== "undefined") {
  const wake = () => {
    for (const video of document.querySelectorAll<HTMLVideoElement>("video[data-autoplay]")) {
      if (!video.paused || !video.getAttribute("src")) continue;
      const box = video.getBoundingClientRect();
      if (box.bottom > 0 && box.top < window.innerHeight && box.width > 0) video.play().catch(() => undefined);
    }
  };
  document.addEventListener("touchend", wake, { capture: true, passive: true });
  document.addEventListener("click", wake, { capture: true, passive: true });
}

function WallClip({ clip, active }: { clip: Clip; active: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  // The strip keeps moving; a clip only downloads and decodes while its card is on screen or about to be
  // (a little ahead, so on phones it already plays when it shows up).
  const onScreen = useInView(ref, { margin: "300px 300px 300px 300px" });
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (!active) {
      video.pause();
      video.currentTime = 0;
      video.removeAttribute("src");
      video.load();
      return;
    }
    if (onScreen) video.play().catch(() => undefined);
    else video.pause();
  }, [active, onScreen]);
  useEffect(() => () => ref.current?.pause(), []);
  return <div className="work-wall-card"><video ref={ref} src={active ? clip.mp4 : undefined} poster={clip.poster} data-autoplay muted loop playsInline preload="none" aria-hidden="true" tabIndex={-1} onCanPlay={() => active && onScreen && ref.current?.play().catch(() => undefined)} /></div>;
}

function WorkWall() {
  const ref = useRef<HTMLElement>(null);
  // Starts a little before the wall scrolls in, so the first clips are ready when it shows.
  const inView = useInView(ref, { margin: "500px 0px 500px 0px" });
  const clips = useClips();
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 620px)").matches);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 620px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // 8 different clips on desktop, 4 on phones (~300 KB each, loaded only while the wall is on screen).
  const uniqueCount = mobile ? 3 : 6;
  const start = mobile ? 5 : 10;
  const available = clips ?? [];
  const selected = available.slice(start, start + uniqueCount);
  const unique = selected.length === uniqueCount ? selected : available.slice(-uniqueCount);
  const wallClips = [...unique, ...unique];
  const active = inView;
  const t = useT();
  const portfolio = usePortfolioHref();
  // No clips (the list failed to load): no wall rather than an empty band.
  if (clips !== null && !clips.length) return null;

  return <section ref={ref} className="section work-wall-section" id="work-wall">
    <SectionTitle eyebrow={t("Scroll-stopping media")} title={<>{t("Content that stops the scroll ")}<em>{t("and drives real revenue.")}</em></>}><p>{t("A snapshot of the high-retention content we produce for real businesses every week.")}</p></SectionTitle>
    <Reveal><div className="work-wall-window">{wallClips.length ? <div className="work-wall-track">{wallClips.map((clip, index) => <WallClip key={`${clip.id}-${index}`} clip={clip} active={active} />)}</div> : <div className="work-wall-fallback" aria-hidden="true" />}</div></Reveal>
    <Reveal className="work-wall-action"><Button href={portfolio()}>{t("See the full portfolio")}</Button><InstagramButton label="More on Instagram" /></Reveal>
  </section>;
}

const industries = [
  ["#Restaurants & Hospitality", -8],
  ["#RealEstate & Lending", 6],
  ["#Automotive & Repair", -12],
  ["#Healthcare & Clinics", 10],
  ["#Home & Industrial Services", -6],
  ["#Faith & Community", 14],
  ["#B2B Professional Services", -4],
] as const;

function IndustriesSection() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const scale = useTransform(scrollYProgress, [0, .5, 1], [.88, 1, .94]);
  const tagUp = useTransform(scrollYProgress, [0, .5, 1], [34, 0, -18]);
  const tagDown = useTransform(scrollYProgress, [0, .5, 1], [-24, 0, 24]);
  const t = useT();

  return <section ref={ref} className="industries-section" id="industries">
    <motion.div className="industries-card" style={{ scale: reduce ? 1 : scale }}>
      <Reveal className="industries-head"><Pill>{t("Tailored solutions")}</Pill><h2>{t("Deep expertise across ")}<em>{t("high-impact industries.")}</em></h2><p>{t("We understand the specific client journeys and sales cycles of your industry.")}</p></Reveal>
      <div className="industry-tags">{industries.map(([label, rotate], index) => <motion.span key={label} style={{ y: reduce ? 0 : index % 2 ? tagDown : tagUp, rotate: reduce ? 0 : rotate }} initial={reduce ? false : { opacity: 0, scale: .8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, amount: .5 }} transition={{ delay: index * .05, duration: .5 }}>{t(label)}</motion.span>)}</div>
      <div className="industries-actions"><Button href="#contact" light>{t("See how we grow your industry")}</Button><InstagramButton /></div>
    </motion.div>
  </section>;
}

function Header() {
  const [open, setOpen] = useState(false);
  const t = useT();
  const { lang } = useLang();
  const portfolio = usePortfolioHref();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  return <header className="site-header" id="header">
    <a className="brand" href="#header">Mark&amp;Ting<span className="brand-dot">.</span></a>
    <nav className="desktop-nav" aria-label="Primary navigation">
      <a href="#services">{t("Services")}</a>
      <a href="#compare">{t("Why us")}</a>
      <a href={portfolio()}>{t("Work")}</a>
      <a href="#reviews">{t("Reviews")}</a>
    </nav>
    <LangSwitch className="lang-switch-header" />
    <Button href={loginHref(lang)}>{t("Login")}</Button>
    <Button href="#contact">{t("Book a call")}</Button>
    <motion.button className="menu-button" type="button" aria-expanded={open} aria-controls="site-menu" onClick={() => setOpen(v => !v)} whileTap={{ scale: .96 }}>
      {t("Menu")} <motion.span animate={{ rotate: open ? 45 : 0 }}>＋</motion.span>
    </motion.button>
    <AnimatePresence>
      {open && <>
        <motion.button aria-label={t("Close menu")} className="menu-backdrop" onClick={() => setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
        <motion.nav className="site-menu" id="site-menu" aria-label="Main navigation" initial={{ opacity: 0, y: -18, scale: .96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12, scale: .97 }} transition={{ duration: .32, ease: [.22, 1, .36, 1] }} style={{ transformOrigin: "top right" }}>
          <div className="site-menu-head"><span>{t("/ Explore")}</span><LangSwitch /></div>
          <div className="site-menu-links">{nav.map(([label, id], index) => <motion.a key={id} href={`#${id}`} onClick={() => setOpen(false)} initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: .035 * index }}>{t(label)}<span>↗︎</span></motion.a>)}</div>
          <motion.a className="button button-light site-menu-login" href={loginHref(lang)} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .035 * nav.length }}><span>{t("Login")}</span><b className="button-icon" aria-hidden="true"><i /><i /><i /><i /></b></motion.a>
        </motion.nav>
      </>}
    </AnimatePresence>
  </header>;
}

type PortfolioItem = {
  id: number;
  category: string;
  title: string;
  image?: string | null;
  youtubeId?: string;
  clip?: string | null;
  clipPoster?: string | null;
};
type Clip = { id: string; mp4: string; poster: string };

// Asked for as soon as the page script runs, before React renders: the gallery's light clips (8 s, ~300 KB each)
// of the portfolio's Content Production videos, served by the portfolio server from R2 (docs/CONTRATO.md).
const portfolioItemsRequest: Promise<PortfolioItem[]> = fetch(PORTFOLIO_ITEMS, { headers: { Accept: "application/json" } })
  .then(response => (response.ok ? response.json() : null))
  .then(payload => payload?.items ?? [])
  .catch(() => []);
const clipsRequest: Promise<Clip[]> = portfolioItemsRequest
  .then(items => items
    .filter((item: { category: string; clip?: string | null; clipPoster?: string | null }) => item.category === "video" && item.clip && item.clipPoster)
    .map(item => ({ id: item.youtubeId ?? String(item.id), mp4: new URL(item.clip!, PORTFOLIO_ITEMS).toString(), poster: new URL(item.clipPoster!, PORTFOLIO_ITEMS).toString() })))
  .catch(() => []);

function usePortfolioItems() {
  const [items, setItems] = useState<PortfolioItem[]>([]);
  useEffect(() => {
    let cancelled = false;
    portfolioItemsRequest.then(list => { if (!cancelled) setItems(list); });
    return () => { cancelled = true; };
  }, []);
  return items;
}

/** The clips of the gallery. null while loading, empty if the list can't load. */
function useClips() {
  const [clips, setClips] = useState<Clip[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    clipsRequest.then(list => { if (!cancelled) setClips([...new Map(list.map(clip => [clip.id, clip])).values()]); });
    return () => { cancelled = true; };
  }, []);
  return clips;
}

function shuffled<T>(list: T[]) {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// A clip shows for 5-7 s (it loops if it ever runs out), then the next one for its cell starts on top and fades in once it plays.
const CLIP_HOLD_MS = [5000, 7000];
const CLIP_FADE_MS = 600;
const CELLS = 5;

type ClipSlot = { clip: Clip; key: number; hold: number; shownAt?: number; leaving?: boolean };
type CellState = { poster: Clip | null; slots: ClipSlot[] };

/** A muted looping clip over its cell. It shows once it really plays; the gallery decides when it leaves. */
function GalleryClip({ clip, leaving, onShow, onFail, onBlocked, onGone }: { clip: Clip; leaving: boolean; onShow: () => void; onFail: () => void; onBlocked: () => void; onGone: () => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [shown, setShown] = useState(false);
  const handlers = useRef({ onShow, onFail, onBlocked, onGone });
  handlers.current = { onShow, onFail, onBlocked, onGone };
  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => handlers.current.onGone(), CLIP_FADE_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);
  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    let started = false;
    const show = () => {
      if (started) return;
      started = true;
      window.clearTimeout(giveUp);
      setShown(true);
      handlers.current.onShow();
    };
    const giveUp = window.setTimeout(() => handlers.current.onFail(), 4000);
    video.muted = true;
    video.addEventListener("playing", show);
    // iPhone Low Power Mode and some browsers refuse to autoplay: the gallery then stays on the covers.
    video.play()?.catch(error => {
      if (error?.name !== "NotAllowedError") return;
      window.clearTimeout(giveUp);
      handlers.current.onBlocked();
    });
    return () => {
      window.clearTimeout(giveUp);
      video.removeEventListener("playing", show);
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, []);
  return <video ref={ref} className={`gallery-clip${shown ? " is-playing" : ""}${leaving ? " is-leaving" : ""}`} src={clip.mp4} poster={clip.poster}
    data-autoplay muted loop playsInline preload="auto" disablePictureInPicture aria-hidden="true" tabIndex={-1} onError={() => handlers.current.onFail()} />;
}

// The loading screen in index.html stays until every visible gallery cell plays a video: at least 0.8 s, at most 6 s.
const SPLASH_MIN_MS = 800;
const SPLASH_MAX_MS = 6000;
let splashHidden = false;
function hideSplash(onHidden: () => void) {
  if (splashHidden) return;
  splashHidden = true;
  window.setTimeout(() => {
    document.getElementById("splash")?.classList.add("is-done");
    document.documentElement.classList.remove("is-loading");
    onHidden();
    window.dispatchEvent(new Event("mt:revealed"));
    window.setTimeout(() => document.getElementById("splash")?.remove(), 900);
  }, Math.max(0, SPLASH_MIN_MS - performance.now()));
}

function HeroGallery({ onReady }: { onReady: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  // Starts a little before the gallery scrolls in, so the first videos are ready when it shows.
  const near = useInView(ref, { margin: "0px 0px 600px 0px" });
  const nearNow = useRef(near);
  nearNow.current = near;
  const clips = useClips();
  // Only if there are no clips: the rotating photos.
  const fallback = clips !== null && clips.length === 0;
  const [blocked, setBlocked] = useState(false);
  // While blocked the refused clips stay in place, so the first tap can start them and the gallery carries on.
  const blockedNow = useRef(false);
  const [phase, setPhase] = useState(0);
  const [photos] = useState(() => shuffled(gallery));
  const [cells, setCells] = useState<CellState[]>(() => Array.from({ length: CELLS }, () => ({ poster: null, slots: [] })));
  const cellsNow = useRef(cells);
  const update = (change: (current: CellState[]) => CellState[]) => {
    const next = change(cellsNow.current);
    if (next === cellsNow.current) return;
    cellsNow.current = next;
    setCells(next);
  };
  const patchSlot = (cell: number, key: number, change: (slot: ClipSlot) => ClipSlot | null) => update(current => {
    const at = current[cell].slots.findIndex(slot => slot.key === key);
    if (at < 0) return current;
    const slots = [...current[cell].slots];
    const slot = change(slots[at]);
    if (slot) slots[at] = slot;
    else slots.splice(at, 1);
    const next = [...current];
    next[cell] = { ...current[cell], slots };
    return next;
  });
  // Each visit plays a random set of clips (20 on desktop, 10 on phones, ~300 KB each), every one once in random order
  // before any repeats, which then come from the browser cache. Never a clip another cell is showing.
  const pool = useRef<Clip[]>([]);
  const bag = useRef<Clip[]>([]);
  const nextClip = (busy: Set<string>) => {
    for (let round = 0; round < 2; round++) {
      const index = bag.current.findIndex(clip => !busy.has(clip.id));
      if (index >= 0) return bag.current.splice(index, 1)[0];
      bag.current = shuffled(pool.current);
    }
    return null;
  };
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 110]);

  useEffect(() => {
    if (!fallback) return;
    const id = window.setInterval(() => setPhase(p => (p + 1) % photos.length), 3800);
    return () => window.clearInterval(id);
  }, [fallback, photos]);

  // Each cell gets a different random clip's first frame right away; its first video is that same clip.
  useEffect(() => {
    if (!clips?.length) return;
    pool.current = shuffled(clips).slice(0, window.matchMedia("(max-width: 900px)").matches ? 10 : 20);
    bag.current = shuffled(pool.current);
    update(current => {
      const busy = new Set<string>();
      return current.map(cell => {
        const poster = nextClip(busy);
        if (poster) busy.add(poster.id);
        return { ...cell, poster };
      });
    });
  }, [clips]);

  // Every visible cell always plays a clip. When one has shown for a while, the next one for that cell starts on top
  // and fades in once it plays (at most 2 starting at once on desktop, 3 on phones and tablets, besides cells still empty).
  // Off with data saver or blocked autoplay (first frames until a tap), in a hidden tab, or once the gallery scrolls out of view.
  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (saveData || blocked || !near || !clips?.length) return;
    // Phones and tablets show 2-3 cells: all of them start at once instead of one after another.
    const startCap = window.matchMedia("(max-width: 900px)").matches ? 3 : 2;
    const step = () => {
      if (document.hidden || !ref.current) return;
      const onPage = [...ref.current.querySelectorAll<HTMLElement>(".gallery-cell")].map(cell => cell.getClientRects().length > 0);
      update(current => {
        const now = Date.now();
        let changed = false;
        const next = current.map(cell => ({ ...cell, slots: [...cell.slots] }));
        let starting = next.reduce((count, cell) => count + cell.slots.filter(slot => !slot.shownAt && !slot.leaving).length, 0);
        const busy = new Set(next.flatMap(cell => cell.slots.map(slot => slot.clip.id)));
        for (const index of shuffled(next.map((_, i) => i))) {
          const cell = next[index];
          if (!onPage[index] || cell.slots.some(slot => !slot.shownAt && !slot.leaving)) continue;
          const top = [...cell.slots].reverse().find(slot => slot.shownAt && !slot.leaving);
          if (top && (now - top.shownAt! < top.hold || starting >= startCap)) continue;
          const clip = !top && cell.poster && !busy.has(cell.poster.id) ? cell.poster : nextClip(busy);
          if (!clip) continue;
          const [low, high] = CLIP_HOLD_MS;
          cell.slots.push({ clip, key: now + index, hold: low + Math.random() * (high - low) });
          busy.add(clip.id);
          starting++;
          changed = true;
        }
        return changed ? next : current;
      });
    };
    step();
    const every = window.setInterval(step, 400);
    return () => {
      window.clearInterval(every);
      if (blockedNow.current) return;
      update(current => (current.some(cell => cell.slots.length) ? current.map(cell => ({ ...cell, slots: [] })) : current));
    };
  }, [near, clips, blocked]);

  // Tells the loading screen when every visible cell plays a video, or right away when there is nothing to wait for:
  // first frames only (data saver, blocked autoplay), photos (no clips), or a page opened away from the gallery.
  useEffect(() => {
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    if (saveData || fallback || blocked) { onReady(); return; }
    if (!ref.current) return;
    const onPage = [...ref.current.querySelectorAll<HTMLElement>(".gallery-cell")].map(cell => cell.getClientRects().length > 0);
    if (onPage.some(Boolean) && cells.every((cell, index) => !onPage[index] || cell.slots.some(slot => slot.shownAt && !slot.leaving))) onReady();
  }, [cells, fallback, blocked]);
  useEffect(() => {
    const timer = window.setTimeout(() => { if (!nearNow.current) onReady(); }, 1200);
    return () => window.clearTimeout(timer);
  }, []);

  const showClip = (cell: number, key: number) => {
    if (blockedNow.current) {
      blockedNow.current = false;
      setBlocked(false);
    }
    update(current => {
      const slot = current[cell].slots.find(item => item.key === key);
      if (!slot) return current;
      const next = [...current];
      next[cell] = { poster: slot.clip, slots: current[cell].slots.map(item => (item.key === key ? { ...item, shownAt: Date.now() } : item)) };
      return next;
    });
    // Once it has faded in, the clip underneath goes.
    window.setTimeout(() => update(current => {
      const at = current[cell].slots.findIndex(item => item.key === key);
      if (at <= 0) return current;
      const next = [...current];
      next[cell] = { ...current[cell], slots: current[cell].slots.slice(at) };
      return next;
    }), CLIP_FADE_MS + 100);
  };

  return <motion.div ref={ref} className="hero-gallery" style={{ y }} aria-label="Mark&Ting production work">
    {cells.map((cell, index) => <div className={`gallery-cell g${index + 1}`} key={index}>
      {fallback
        ? <AnimatePresence mode="popLayout">
          <motion.img key={photos[(phase + index) % photos.length]} src={`${A}${photos[(phase + index) % photos.length]}`} alt="Mark&Ting production work" initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: .9, ease: "easeOut" }} />
        </AnimatePresence>
        : cell.poster && <img src={cell.poster.poster} alt="" />}
      {cell.slots.map(slot => <GalleryClip key={slot.key} clip={slot.clip} leaving={Boolean(slot.leaving)}
        onShow={() => showClip(index, slot.key)}
        onFail={() => patchSlot(index, slot.key, item => ({ ...item, leaving: true }))}
        onBlocked={() => { blockedNow.current = true; setBlocked(true); }}
        onGone={() => patchSlot(index, slot.key, () => null)} />)}
    </div>)}
  </motion.div>;
}

function App() {
  const [faq, setFaq] = useState(0);
  const [contactStatus, setContactStatus] = useState({ state: "idle", message: "" });
  const portfolioItems = usePortfolioItems();
  const [lang, setLangState] = useState<Lang>(initialLang);
  const t = (text: string) => translate(lang, text);
  const setLang = (next: Lang) => {
    setLangState(next);
    try { localStorage.setItem(LANG_KEY, next); } catch { /* private mode */ }
    const url = new URL(window.location.href);
    if (url.searchParams.has("lang")) { url.searchParams.delete("lang"); window.history.replaceState(null, "", url.toString()); }
  };
  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = translate(lang, "Mark&Ting Agency | Content, Ads & Automation");
  }, [lang]);
  // The hero plays its entrance once the loading screen lifts.
  const [revealed, setRevealed] = useState(false);
  // Every "Book Strategy Call" / "Book a call" / "Start now" (any #contact link) opens the form over the page.
  const [contactOpen, setContactOpen] = useState(false);
  const contactOpener = useRef<HTMLElement | null>(null);
  const closeContact = () => {
    setContactOpen(false);
    if (window.location.hash === "#contact") window.history.replaceState(null, "", window.location.pathname + window.location.search);
    window.setTimeout(() => contactOpener.current?.focus?.(), 50);
  };
  useEffect(() => {
    const open = () => { contactOpener.current = document.activeElement as HTMLElement | null; setContactOpen(true); };
    const onClick = (event: MouseEvent) => {
      const href = (event.target as Element | null)?.closest?.("a[href]")?.getAttribute("href") ?? "";
      if (href === "#contact" || href.endsWith("/#contact")) { event.preventDefault(); open(); }
    };
    // Typing or following a /#contact link while the page is open opens it too.
    const onHash = () => { if (window.location.hash === "#contact") open(); };
    document.addEventListener("click", onClick, true);
    window.addEventListener("mt:open-contact", open);
    window.addEventListener("hashchange", onHash);
    return () => { document.removeEventListener("click", onClick, true); window.removeEventListener("mt:open-contact", open); window.removeEventListener("hashchange", onHash); };
  }, []);
  useEffect(() => {
    if (!contactOpen) return;
    document.documentElement.classList.add("is-modal");
    window.dispatchEvent(new Event("mt:modal-open"));
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") closeContact(); };
    window.addEventListener("keydown", onKey);
    const focus = window.setTimeout(() => document.querySelector<HTMLInputElement>(".contact-modal input[name=name]")?.focus(), 380);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(focus);
      document.documentElement.classList.remove("is-modal");
      window.dispatchEvent(new Event("mt:modal-close"));
    };
  }, [contactOpen]);
  const reveal = () => hideSplash(() => setRevealed(true));
  useEffect(() => {
    const timer = window.setTimeout(reveal, Math.max(0, SPLASH_MAX_MS - performance.now()));
    return () => window.clearTimeout(timer);
  }, []);
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    // No scrolling under the loading screen.
    if (document.documentElement.classList.contains("is-loading")) lenis.stop();
    // When the loading screen lifts: the top of the page, or the section the link asked for (e.g. /#contact).
    const start = () => {
      lenis.start();
      if (window.location.hash === "#contact") {
        lenis.scrollTo(0, { immediate: true });
        window.dispatchEvent(new Event("mt:open-contact"));
        return;
      }
      const target = window.location.hash.length > 1 ? document.getElementById(decodeURIComponent(window.location.hash.slice(1))) : null;
      if (target) window.setTimeout(() => lenis.scrollTo(target, { offset: -90 }), 150);
      else lenis.scrollTo(0, { immediate: true });
    };
    window.addEventListener("mt:revealed", start);
    const pause = () => lenis.stop();
    const resume = () => lenis.start();
    window.addEventListener("mt:modal-open", pause);
    window.addEventListener("mt:modal-close", resume);
    let frame = 0;
    const raf = (time: number) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); window.removeEventListener("mt:revealed", start); window.removeEventListener("mt:modal-open", pause); window.removeEventListener("mt:modal-close", resume); lenis.destroy(); };
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const form = event.currentTarget;
    const subject = encodeURIComponent(`Strategy call request from ${data.get("name")}`);
    const body = encodeURIComponent(`Name: ${data.get("name")}\nEmail: ${data.get("email")}\nWebsite: ${data.get("website")}\nInterested in: ${data.get("model")}\n\n${data.get("message")}`);
    const mailto = `mailto:contact@markandtingagency.com?subject=${subject}&body=${body}`;

    setContactStatus({ state: "sending", message: "" });
    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.get("name"),
          email: data.get("email"),
          website: data.get("website"),
          model: data.get("model"),
          message: data.get("message"),
          company: data.get("company"),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.ok === false) throw new Error("Contact request failed.");
      form.reset();
      setContactStatus({ state: "success", message: t("Thanks! We'll be in touch soon.") });
    } catch {
      setContactStatus({ state: "error", message: t("We could not send automatically. Opening your email app...") });
      window.location.href = mailto;
    }
  };

  return <LangContext.Provider value={{ lang, setLang }}>
    <ScrollProgress />
    <a className="announcement" href="#contact"><span>✦</span> {t("Growth systems for businesses ready to scale")} <span>↗︎</span></a>
    <Header />
    <main>
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <motion.div className="eyebrow-row" initial={{ opacity: 0, y: -10 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ duration: .7 }}><Pill>{t("Content + Ads + Automation")}</Pill></motion.div>
          <motion.h1 id="hero-title" initial="hidden" animate={revealed ? "show" : "hidden"}><motion.span variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: .75, ease: [.22, 1, .36, 1] } } }}>{t("Build attention.")}</motion.span><br /><motion.em variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: .75, delay: .12, ease: [.22, 1, .36, 1] } } }}>{t("Turn it into revenue.")}</motion.em></motion.h1>
          <motion.p initial={{ opacity: 0, y: 25 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ duration: .8, delay: .15 }}>{t("We help businesses grow with content, paid ads, automation, and AI systems that capture leads, follow up faster, and turn attention into revenue.")}</motion.p>
          <motion.div className="hero-actions" initial={{ opacity: 0, y: 20 }} animate={revealed ? { opacity: 1, y: 0 } : undefined} transition={{ delay: .28 }}><Button href="#contact">{t("Book Strategy Call")}</Button><Button href={portfolioHref(lang)} light>{t("See Our Work")}</Button><InstagramButton /></motion.div>
          <motion.div className="trust-line" initial={{ opacity: 0 }} animate={revealed ? { opacity: 1 } : undefined} transition={{ delay: .55 }}><span className="avatar-stack"><img src={`${A}eU0eE1uH2s48zEZ2CvBgNbNmM1Y.jpeg`} alt="" /><img src={`${A}OS4kQqWVoAz7BhW5muuEOyaB6s.jpeg`} alt="" /><img src={`${A}LP3tY1VU63Loeiw2m8ahNTubMwQ.jpeg`} alt="" /></span><span><b>★★★★★</b><strong>{t("Built for businesses that want more than marketing")}</strong><small>{t("They want systems that grow with them.")}</small></span></motion.div>
        </div>
        <HeroGallery onReady={reveal} />
      </section>

      <section className="statement-section">
        <Reveal><p>{t("Built for businesses that want more than marketing")}</p><h2>{t("They want systems that grow with them.")}</h2></Reveal>
      </section>

      <section className="logo-section" aria-labelledby="logo-section-title">
        <Reveal><p id="logo-section-title">{t("Trusted by businesses like yours")}</p></Reveal>
        <RandomLogoGrid />
      </section>

      <section className="section" id="services">
        <SectionTitle eyebrow={t("Services")} title={<>{t("What we can ")}<em>{t("do for you.")}</em></>}><p>{t("Content, paid ads, websites, automation, and AI infrastructure built to attract qualified attention and turn interest into revenue.")}</p><strong>{t("We do not just post content. We build a connected growth engine.")}</strong></SectionTitle>
        <div className="services-list">{services.map((service, i) => <Reveal key={service.title} delay={(i % 2) * .08}><TiltCard className={`service-card ${i % 2 ? "reverse" : ""}`} lift={-4}><div className="service-copy"><span className="service-badge">{t(service.badge)}</span><h3>{t(service.title)}</h3><p>{t(service.text)}</p><ul>{service.bullets.map(item => <li key={item}>{t(item)}</li>)}</ul><div className="service-actions"><Button href="#contact">{t("Start now")}</Button>{(!portfolioItems.length || portfolioItems.some(item => item.category === service.work)) && <Button href={portfolioHref(lang, `#${service.work}`)} light>{t("See work")}</Button>}</div></div><div className={`service-image service-visual-${service.visual}`}><ServiceVisual service={service} items={portfolioItems} /></div></TiltCard></Reveal>)}</div>
      </section>

      <WorkWall />

      <ComparisonSection />

      <IndustriesSection />

      <section className="section" id="reviews">
        <SectionTitle eyebrow={t("Reviews")} title={<>{t("What Clients")}<br />{t("Value Most.")}</>}><strong>{t("Built for businesses that want more than marketing — they want systems that help them grow with consistency.")}</strong></SectionTitle>
        <div className="review-grid">{reviews.map(([quote, person, role, image], i) => <Reveal key={person} delay={(i % 2) * .07}><article><div className="quote"><span>4.9 <i>★</i> {t("Rating")}</span><blockquote>“{quote}”</blockquote></div><div className="person"><img src={`${A}${image}`} alt={person} /><p><strong>{person}</strong><small>{role}</small></p></div></article></Reveal>)}</div>
      </section>

      <section className="section" id="team">
        <SectionTitle eyebrow={t("The people behind the results")} title={<>{t("The team behind ")}<em>{t("your growth.")}</em></>}><p>{t("Strategy, content, design and technology — built by the people you work with directly.")}</p></SectionTitle>
        <div className="team-grid">{team.map((member, index) => <TeamMember key={member.name} {...member} delay={(index % 3) * .06} />)}</div>
        <Reveal className="team-actions"><Button href="#contact">{t("Book Strategy Call")}</Button><InstagramButton /></Reveal>
      </section>

      <section className="section faq-section" id="faqs">
        <SectionTitle eyebrow={t("FAQs")} title={t("Frequently Asked Questions.")}><p>{t("Everything you need to know about working with Mark & Ting and how we help businesses grow.")}</p></SectionTitle>
        <div className="faq-list">{faqs.map(([question, answer], i) => <Reveal key={question}><article className={faq === i ? "open" : ""}><button onClick={() => setFaq(faq === i ? -1 : i)} aria-expanded={faq === i}><span>{t(question)}</span><motion.b aria-hidden="true" animate={{ rotate: faq === i ? 45 : 0 }}>＋</motion.b></button><AnimatePresence initial={false}>{faq === i && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}><p>{t(answer)}</p></motion.div>}</AnimatePresence></article></Reveal>)}</div>
      </section>

      <section className="section" id="onboarding">
        <SectionTitle eyebrow={t("Onboarding")} title={<>{t("Okay… But What Happens")}<br />{t("After I Say Yes?")}</>} />
        <div className="steps-grid">{steps.map(([title, text], i) => <Reveal key={title} delay={(i % 2) * .08}><TiltCard lift={-6}><b>{String(i + 1).padStart(2, "0")}</b><h3>{t(title)}</h3><p>{t(text)}</p></TiltCard></Reveal>)}</div>
      </section>

    </main>
    <Footer />
    <AnimatePresence>{contactOpen && <motion.div className="contact-modal" role="dialog" aria-modal="true" aria-labelledby="contact-modal-title" data-lenis-prevent
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .25 }}
      onMouseDown={event => { if (event.target === event.currentTarget) closeContact(); }}>
      <motion.div className="contact-modal-panel" initial={{ y: 36, scale: .98 }} animate={{ y: 0, scale: 1 }} exit={{ y: 24, opacity: 0 }} transition={{ duration: .38, ease: [.22, 1, .36, 1] }}>
        <button type="button" className="contact-modal-close" onClick={closeContact} aria-label={t("Close")}>✕</button>
        <div className="contact-modal-head"><Pill>{t("Book a strategy call")}</Pill><h2 id="contact-modal-title">{t("Stop losing revenue to ")}<em>{t("inconsistent marketing.")}</em></h2><p>{t("Let’s talk about the right system for you.")}</p></div>
        <div className="contact-card"><div className="contact-details"><div><small>{t("Email us")}</small><a href="mailto:contact@markandtingagency.com">contact@markandtingagency.com</a></div><div><small>{t("Call or text")}</small><a href="tel:+19185807197">+1 918 580 7197</a></div><div><small>{t("More work")}</small><a href={INSTAGRAM_URL} target="_blank" rel="noopener">{t("@markandtingagency on Instagram ↗︎")}</a></div></div><form onSubmit={submit}><label>{t("Name")}<input name="name" autoComplete="name" placeholder={t("Your name")} required /></label><label>{t("E-mail*")}<input type="email" name="email" autoComplete="email" placeholder={t("Your email")} required /></label><label>{t("Website")}<input type="url" name="website" placeholder={t("Your website")} /></label><label className="contact-honeypot" aria-hidden="true">Company<input name="company" tabIndex={-1} autoComplete="off" /></label><fieldset className="contact-interest"><legend>{t("I'm interested in")}</legend>{interests.map((option, i) => <label key={option}><input type="radio" name="model" value={option} defaultChecked={i === 0} /> {t(option)}</label>)}</fieldset><label>{t("Message")}<textarea name="message" placeholder={t("Your message")} rows={5} required /></label><motion.button type="submit" disabled={contactStatus.state === "sending"} whileHover={{ scale: 1.01 }} whileTap={{ scale: .98 }}>{t(contactStatus.state === "sending" ? "Sending..." : "Schedule Call")} <span>↗︎</span></motion.button>{contactStatus.message && <p className={`contact-status ${contactStatus.state === "error" ? "error" : ""}`}>{contactStatus.message}</p>}</form></div>
      </motion.div>
    </motion.div>}</AnimatePresence>
  </LangContext.Provider>;
}

function TeamMember({ image, name, role, bio, delay }: { image: string; name: string; role: string; bio: string; delay: number }) {
  const t = useT();
  return <Reveal delay={delay}><article className="team-card"><div className="team-image"><motion.img src={image} alt={name} whileHover={{ scale: 1.04 }} transition={{ duration: .55 }} /></div><div className="team-copy"><Pill>{t(role)}</Pill><h3>{name}</h3><p>{t(bio)}</p></div></article></Reveal>;
}

function Footer() {
  const t = useT();
  const portfolio = usePortfolioHref();
  return <footer><div className="footer-top"><div><h2>{t("Let’s Grow Your Business Smarter.")}</h2><p>{t("Content, ads, and automation built to help Hispanic businesses and realtors grow with more consistency, visibility, and results.")}</p><a className="footer-email" href="mailto:contact@markandtingagency.com">{t("Your Email")} <b>↗︎</b></a><a className="footer-phone" href="tel:+19185807197">+1 918 580 7197</a></div><div className="footer-links"><div><strong>{t("/Explore")}</strong><a href="#services">{t("Services")}</a><a href="#compare">{t("Why us")}</a><a href={portfolio()}>{t("Work")}</a><a href="#reviews">{t("Reviews")}</a><a href="#team">{t("Team")}</a><a href="#faqs">{t("FAQs")}</a></div><div><strong>{t("/Resources")}</strong><a href={portfolio()}>{t("All Projects")}</a><a href="/legals/privacy-policy/">{t("Privacy Policy")}</a><a href="/legals/terms-of-service/">{t("Terms of Service")}</a></div><div><strong>{t("/Socials")}</strong><a href="https://www.tiktok.com/@markandtingagency">TikTok</a><a href="https://www.facebook.com/people/MarkTing-Growth-Agency/61583170465614/">Facebook</a><a href="https://www.instagram.com/markandtingagency">Instagram</a><a href="https://www.linkedin.com/company/markandtingagency/">LinkedIn</a><a href="https://linktr.ee/MarkandTingAgency">linktr.ee</a></div></div></div><Reveal><div className="footer-wordmark">Mark&amp;Ting<span className="brand-dot">.</span></div></Reveal><div className="copyright">{t("© 2026 Mark&Ting. All rights reserved.")}</div></footer>;
}

export default App;
